import { Seo } from "@/components/Seo";
import { company, addressText } from "@/content/site";

/**
 * Public legal pages (Privacy Policy + Terms of Service).
 *
 * These are tailored to what the site actually does (marketing pages + a
 * tracking lookup + driver/carrier applications + a staff/partner portal backed
 * by Supabase, with Fleetio/Samsara/QuickManage/Mapbox/Resend as processors).
 * They are written to be accurate and launch-ready, but BB should still have
 * counsel review them before relying on them for any specific legal claim.
 */
const EFFECTIVE_DATE = "June 26, 2026";

function LegalShell({
  title,
  intro,
  children,
}: {
  title: string;
  intro: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <Seo title={`${title} | ${company.name}`} description={intro} />
      <article className="container max-w-prose py-12 sm:py-16">
        <h1 className="break-words text-3xl font-bold text-brand-900">{title}</h1>
        <p className="mt-2 text-sm text-brand-400">Last updated: {EFFECTIVE_DATE}</p>
        <p className="mt-6 break-words text-brand-700">{intro}</p>
        <div className="mt-8 space-y-8 break-words text-brand-700">{children}</div>
      </article>
    </>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-brand-900">{heading}</h2>
      {children}
    </section>
  );
}

export function Privacy() {
  return (
    <LegalShell
      title="Privacy Policy"
      intro={`This Privacy Policy explains how ${company.name} ("Bull & Bear," "we," "us," or "our") collects, uses, and protects information when you visit our website, request a quote, track a shipment, apply to drive or partner with us, or use our customer and staff portal.`}
    >
      <Section heading="Information we collect">
        <p>We collect information you provide directly and a limited amount of technical information collected automatically:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Contact &amp; inquiry details</strong> — name, company, email, phone, and the
            content of any message when you use our contact, quote, or careers forms.
          </li>
          <li>
            <strong>Application information</strong> — for driver and owner-operator applications:
            employment and experience details, license/endorsement and DOT-related information, and
            any documents you choose to upload (for example a CDL, medical card, or insurance).
          </li>
          <li>
            <strong>Shipment references</strong> — load, order, or tracking numbers you enter to look
            up status, plus the results returned for that lookup.
          </li>
          <li>
            <strong>Account information</strong> — for staff and authorized partners with portal
            access: email, role, and authentication data managed by our identity provider.
          </li>
          <li>
            <strong>Technical &amp; usage data</strong> — standard server logs and device/browser
            information (such as IP address and pages viewed) used to operate and secure the site.
          </li>
        </ul>
      </Section>

      <Section heading="How we use information">
        <ul className="list-disc space-y-2 pl-6">
          <li>To respond to inquiries, prepare quotes, and provide shipment status.</li>
          <li>To evaluate and process employment and carrier applications.</li>
          <li>To operate, secure, and improve the website and portal.</li>
          <li>To comply with legal, safety, insurance, and DOT/FMCSA recordkeeping obligations.</li>
          <li>To detect, prevent, and respond to fraud, abuse, or security incidents.</li>
        </ul>
        <p>
          We do not sell your personal information, and we do not use the contact or application
          information you submit for third-party advertising.
        </p>
      </Section>

      <Section heading="Service providers we share with">
        <p>
          We share information only with vendors that help us run the business, under agreements that
          limit their use of it to providing services to us:
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li><strong>Supabase</strong> — application database, authentication, and document storage.</li>
          <li><strong>Samsara</strong> — vehicle location and hours-of-service data used for tracking.</li>
          <li><strong>Fleetio</strong> — fleet and maintenance records.</li>
          <li><strong>QuickManage</strong> — load and order management.</li>
          <li><strong>Mapbox</strong> — maps used in the tracking experience.</li>
          <li><strong>Email/hosting providers</strong> — to deliver notifications and serve the site.</li>
        </ul>
        <p>
          We may also disclose information when required by law, to enforce our agreements, or to
          protect the rights, property, or safety of Bull &amp; Bear, our customers, or the public.
        </p>
      </Section>

      <Section heading="Cookies and similar technologies">
        <p>
          We use only the cookies and local storage necessary to operate the site and keep you signed
          in to the portal. We do not use third-party advertising or cross-site tracking cookies. You
          can control cookies through your browser settings, though some features may not work without
          them.
        </p>
      </Section>

      <Section heading="Data retention">
        <p>
          We keep personal information for as long as needed for the purposes described above and to
          meet legal, tax, insurance, and DOT/FMCSA retention requirements, after which it is deleted
          or anonymized.
        </p>
      </Section>

      <Section heading="How we protect information">
        <p>
          Access is restricted on a least-privilege basis and protected by authentication and
          row-level security. Uploaded documents are stored in private storage and served only through
          short-lived, signed links. No method of transmission or storage is completely secure, but we
          take reasonable measures to safeguard your information.
        </p>
      </Section>

      <Section heading="Your choices and rights">
        <p>
          You may request access to, correction of, or deletion of the personal information we hold
          about you, subject to records we are legally required to retain. To make a request, contact
          us using the details below. We will respond within a reasonable timeframe.
        </p>
      </Section>

      <Section heading="Children's privacy">
        <p>
          This site is intended for businesses and adults. We do not knowingly collect personal
          information from children under 13.
        </p>
      </Section>

      <Section heading="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be reflected by
          updating the "Last updated" date above.
        </p>
      </Section>

      <Section heading="Contact us">
        <p>
          Questions or privacy requests? Contact {company.name} at{" "}
          <a className="text-accent-600 underline" href={`mailto:${company.email}`}>
            {company.email}
          </a>{" "}
          or {company.phone}, {addressText}.
        </p>
      </Section>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell
      title="Terms of Service"
      intro={`These Terms of Service ("Terms") govern your use of the ${company.name} website and portal. By accessing or using the site, you agree to these Terms. If you do not agree, please do not use the site.`}
    >
      <Section heading="Use of the site">
        <p>
          You may use the site for lawful purposes only. You agree not to misuse it, attempt to gain
          unauthorized access, interfere with its operation, or use it to infringe the rights of
          others.
        </p>
      </Section>

      <Section heading="Quotes, tracking, and information">
        <p>
          Content on the site, including rates, service descriptions, availability, and tracking
          information, is provided for general informational purposes. Submitting a quote request or
          inquiry does not create a binding contract; transportation services are governed by the
          separate rate confirmation, broker-carrier agreement, or contract of carriage agreed between
          the parties. Tracking data is provided "as available" and may be delayed or estimated.
        </p>
      </Section>

      <Section heading="Accounts and portal access">
        <p>
          Some areas are restricted to authorized staff and partners. If you are granted access, you
          are responsible for maintaining the confidentiality of your credentials and for all activity
          under your account. Notify us immediately of any unauthorized use.
        </p>
      </Section>

      <Section heading="Applications">
        <p>
          Information submitted through driver, owner-operator, or carrier applications must be true
          and complete. Submitting an application does not guarantee employment, engagement, or a
          contract.
        </p>
      </Section>

      <Section heading="Intellectual property">
        <p>
          The site and its content — including the Bull &amp; Bear name, logo, text, and graphics — are
          owned by {company.name} or its licensors and are protected by applicable laws. You may not
          copy, reproduce, or use them without our prior written permission. Third-party logos shown on
          the site are the property of their respective owners.
        </p>
      </Section>

      <Section heading="Third-party services and links">
        <p>
          The site relies on and may link to third-party services. We are not responsible for the
          content, availability, or privacy practices of third parties.
        </p>
      </Section>

      <Section heading="Disclaimers">
        <p>
          The site is provided "as is" and "as available" without warranties of any kind, express or
          implied, including merchantability, fitness for a particular purpose, and non-infringement.
          We do not warrant that the site will be uninterrupted, error-free, or secure.
        </p>
      </Section>

      <Section heading="Limitation of liability">
        <p>
          To the fullest extent permitted by law, {company.name} will not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or any loss of profits or data,
          arising out of or related to your use of the site.
        </p>
      </Section>

      <Section heading="Indemnification">
        <p>
          You agree to indemnify and hold harmless {company.name} from any claims, damages, or expenses
          arising out of your misuse of the site or violation of these Terms.
        </p>
      </Section>

      <Section heading="Governing law">
        <p>
          These Terms are governed by the laws of the State of Illinois, without regard to its conflict
          of laws rules. Any dispute will be subject to the exclusive jurisdiction of the state and
          federal courts located in Illinois.
        </p>
      </Section>

      <Section heading="Changes to these Terms">
        <p>
          We may update these Terms from time to time. Continued use of the site after changes take
          effect constitutes acceptance of the updated Terms.
        </p>
      </Section>

      <Section heading="Contact us">
        <p>
          Questions about these Terms? Contact {company.name} at{" "}
          <a className="text-accent-600 underline" href={`mailto:${company.email}`}>
            {company.email}
          </a>{" "}
          or {company.phone}, {addressText}.
        </p>
      </Section>
    </LegalShell>
  );
}
